import { cn } from '@/shared/helpers/classnames'
import { getTypedBlocksByTypes } from '@/shared/helpers/compoundPattern'

import { createContext, forwardRef, useContext, useId, useMemo } from 'react'

import {
  Content as RadixAccordionContent,
  Header as RadixAccordionHeader,
  Item as RadixAccordionItem,
} from '@radix-ui/react-accordion'

import { BlockContext, type BlockContextValue } from './BlockContext'
import type { BlockContentVariant, BlockItemBlocksTypes, BlockItemProps } from './types'

// Context local pour passer contentVariant à BlockContent
export type BlockItemContextValue = {
  contentVariant: BlockContentVariant
}

export const BlockItemContext = createContext<BlockItemContextValue>({
  contentVariant: 'accent',
})

export const BlockItem = forwardRef<HTMLDivElement, BlockItemProps>(
  (
    {
      children,
      value,
      disabled,
      contentVariant,
      defaultOpen: _defaultOpen = false, // Extracted by parent BlockListRoot, not used here
    },
    ref,
  ) => {
    const uniqueId = useId()
    const blockContext = useContext(BlockContext)

    const effectiveContentVariant = contentVariant ?? blockContext.contentVariant

    const itemContextValue = useMemo<BlockItemContextValue>(
      () => ({ contentVariant: effectiveContentVariant }),
      [effectiveContentVariant],
    )

    const blockContextValue = useMemo<BlockContextValue>(
      () => ({ ...blockContext, disabled, contentVariant: effectiveContentVariant }),
      [blockContext, effectiveContentVariant, disabled],
    )

    const blocks = getTypedBlocksByTypes<BlockItemBlocksTypes>(children)

    const header = blocks.BlockHeader

    return (
      <BlockContext value={blockContextValue}>
        <BlockItemContext value={itemContextValue}>
          <RadixAccordionItem
            ref={ref}
            value={value ?? uniqueId}
            disabled={disabled}
            className={cn('block-item', disabled && 'block-item--disabled')}
          >
            <RadixAccordionHeader>{header}</RadixAccordionHeader>
            <RadixAccordionContent className="block-content-wrapper">
              {blocks.BlockContent}
            </RadixAccordionContent>
          </RadixAccordionItem>
        </BlockItemContext>
      </BlockContext>
    )
  },
)

BlockItem.displayName = 'BlockItem'
